import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Button from '../ui/Button';
import ActiveContractCard from './ActiveContractCard';
import ContractHistoryTable from './ContractHistoryTable';
import NewContractModal from './NewContractModal';
import EditContractModal from './EditContractModal';

interface ContractsTabProps {
  clientId: string;
}

interface Contract {
  id: string;
  client_id: string;
  type: 'cold_calling' | 'mailing' | 'growth';
  billing_unit: 'days' | 'campaign' | 'package';
  signed_date: string | null;
  start_date: string;
  end_date: string | null;
  days_per_month: number | null;
  monthly_price: number | null;
  price_per_day: number | null;
  is_active: boolean;
  version: number;
  notes: string | null;
}

export interface ContractData {
  type: 'cold_calling' | 'mailing' | 'growth';
  billing_unit: 'days' | 'campaign' | 'package';
  signed_date?: string;
  start_date: string;
  end_date?: string;
  days_per_month?: number;
  monthly_price?: number;
  notes?: string;
}

const ContractsTab: React.FC<ContractsTabProps> = ({ clientId }) => {
  const [activeContracts, setActiveContracts] = useState<Contract[]>([]);
  const [historyContracts, setHistoryContracts] = useState<Contract[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [renewalContract, setRenewalContract] = useState<Contract | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);

  useEffect(() => {
    fetchContracts();
  }, [clientId]);

  const fetchContracts = async () => {
    try {
      const { data, error } = await supabase
        .from('client_contracts')
        .select('*')
        .eq('client_id', clientId)
        .order('type', { ascending: true })
        .order('version', { ascending: false });

      if (error) throw error;

      const active = (data || []).filter((c) => c.is_active);
      const history = (data || []).filter((c) => !c.is_active);

      setActiveContracts(active);
      setHistoryContracts(history);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  const handleSaveContract = async (contractData: ContractData) => {
    try {
      const { error } = await supabase.from('client_contracts').insert({
        client_id: clientId,
        type: contractData.type,
        billing_unit: contractData.billing_unit,
        signed_date: contractData.signed_date || null,
        start_date: contractData.start_date,
        end_date: contractData.end_date || null,
        days_per_month: contractData.days_per_month || null,
        monthly_price: contractData.monthly_price || null,
        notes: contractData.notes || null,
        is_active: true,
        version: 1,
      });

      if (error) throw error;

      await fetchContracts();
    } catch (error) {
      console.error('Error saving contract:', error);
      throw error;
    }
  };

  const handleRenewContract = (contract: Contract) => {
    setRenewalContract(contract);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setRenewalContract(null);
  };

  const handleEditContract = (contract: Contract) => {
    setEditingContract(contract);
    setIsEditModalOpen(true);
  };

  const handleUpdateContract = async (contractId: string, updates: Partial<Contract>) => {
    try {
      const { error } = await supabase
        .from('client_contracts')
        .update(updates)
        .eq('id', contractId);

      if (error) throw error;

      await fetchContracts();
      setIsEditModalOpen(false);
      setEditingContract(null);
    } catch (error) {
      console.error('Error updating contract:', error);
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Contrats actifs</h2>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Nouveau contrat
        </Button>
      </div>

      {activeContracts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeContracts.map((contract) => (
            <ActiveContractCard
              key={contract.id}
              contract={contract}
              onRenew={handleRenewContract}
              onEdit={handleEditContract}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500">Aucun contrat actif</p>
        </div>
      )}

      <div className="pt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique</h3>
        <ContractHistoryTable contracts={historyContracts} onEdit={handleEditContract} />
      </div>

      <NewContractModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveContract}
        renewalData={renewalContract}
      />

      {editingContract && (
        <EditContractModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingContract(null);
          }}
          onSave={handleUpdateContract}
          contract={editingContract}
        />
      )}
    </div>
  );
};

export default ContractsTab;
